/** Deterministic teaching scenarios. These are not advertiser observations or forecasts. */
export type MeasurementLabKind = 'mix' | 'lag' | 'marginal'

export const LAB_TITLES: Record<MeasurementLabKind, string> = {
  mix: 'The audience-mix reversal lab',
  lag: 'The conversion-maturity lab',
  marginal: 'The next-dollar lab',
}

/** Rates within each pre-existing audience segment remain fixed as allocation changes. */
export function audienceMix(
  aWarm: number,
  bWarm: number,
): { a: number; b: number; aStandard: number; bStandard: number } {
  return {
    a: (1 - aWarm / 100) * 1 + (aWarm / 100) * 8,
    b: (1 - bWarm / 100) * 1.5 + (bWarm / 100) * 9,
    aStandard: 4.5,
    bStandard: 5.25,
  }
}

export const MATURITY_COUNTS = [40, 60, 75, 84, 90, 96, 100] as const

/** Adjust a fixed click cohort using an assumed historical completeness fraction. */
export function conversionMaturity(
  day: number,
  completeness: number,
): {
  observed: number
  rawCpa: number
  projected: number
  adjustedCpa: number
} {
  const observed = MATURITY_COUNTS[day - 1] ?? MATURITY_COUNTS[0]
  return {
    observed,
    rawCpa: 3000 / observed,
    projected: observed / (completeness / 100),
    adjustedCpa: (3000 * completeness) / (100 * observed),
  }
}

/** Assumed incremental revenue response, in dollars, for one fixed reporting horizon. */
export function responseRevenue(spend: number): number {
  return (60000 * spend) / (10000 + spend)
}

/** Finite budget-step return differs from the derivative at the starting spend. */
export function marginalReturn(
  spend: number,
  step: number,
  margin: number,
): {
  revenue: number
  extraRevenue: number
  average: number
  marginal: number
  stepReturn: number
  contribution: number
  hurdle: number
} {
  const revenue = responseRevenue(spend)
  const extraRevenue = responseRevenue(spend + step) - revenue
  return {
    revenue,
    extraRevenue,
    average: revenue / spend,
    marginal: 600000000 / (10000 + spend) ** 2,
    stepReturn: extraRevenue / step,
    contribution: extraRevenue * (margin / 100) - step,
    hurdle: 100 / margin,
  }
}
